import { Button } from "@/components/ui/button";
import { trackDonationClick } from "@/lib/trackDonation";

/**
 * SupportButtons — Venmo / PayPal donation links.
 *
 * Accessibility / keyboard testing checklist:
 * - Tab order: from the Footer "Support" column heading, Tab should land on
 *   Venmo first, then PayPal. Both must show a visible focus ring
 *   (`focus-visible:ring-[3px] ring-accent`) against the dark footer bg.
 * - Enter/Space on a focused link must open the provider in a NEW TAB
 *   (target="_blank" + rel="noopener noreferrer").
 * - Screen readers should announce: full aria-label ("Support Rob with a tip
 *   via Venmo/PayPal..."), the visible label, the sr-only "(opens in a new
 *   tab)" suffix, and the shared aria-describedby note
 *   #support-buttons-newtab-note.
 * - The wrapping container exposes id="support-donations" so keyboard
 *   users can jump straight here via a skip link.
 * - When changing button colors, re-check WCAG 4.5:1 contrast for the white
 *   label on the brand background AND for the focus ring against the footer.
 * - Do NOT remove the sr-only span or aria-describedby — visible text alone
 *   does not announce new-tab behavior.
 */
interface SupportButtonsProps {
  variant?: "compact" | "full";
}

export const SupportButtons = ({ variant = "compact" }: SupportButtonsProps) => {
  const buttons = (
    <div className="flex flex-col sm:flex-row gap-3 w-full">
      <p id="support-buttons-newtab-note" className="sr-only">
        Venmo and PayPal links open in a new tab.
      </p>
      <Button
        asChild
        className="flex-1 rounded-md text-white shadow-sm transition-all duration-200 hover:brightness-110 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:brightness-95 active:shadow-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        style={{ backgroundColor: "#3D95CE" }}
      >
        <a
          href="https://venmo.com/u/roblasa"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Support Rob with a tip via Venmo (opens Venmo in a new tab)"
          aria-describedby="support-buttons-newtab-note"
          onClick={() => trackDonationClick("venmo", "footer")}
        >
          Venmo
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Button>
      <Button
        asChild
        className="flex-1 rounded-md text-white shadow-sm transition-all duration-200 hover:brightness-110 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:brightness-95 active:shadow-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        style={{ backgroundColor: "#003087" }}
      >
        <a
          href="https://paypal.me/roblasa"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Support Rob with a tip via PayPal (opens PayPal in a new tab)"
          aria-describedby="support-buttons-newtab-note"
          onClick={() => trackDonationClick("paypal", "footer")}
        >
          PayPal
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Button>
    </div>
  );

  if (variant === "compact") {
    return buttons;
  }

  return (
    <div className="max-w-md mx-auto text-center space-y-4">
      <h3 className="text-2xl font-display font-bold text-foreground">
        Support this work
      </h3>
      <p className="text-base font-bold text-accent">
        No program. No pitch. Just a thank you.
      </p>
      <p className="text-muted-foreground">
        If something here landed, you can buy me a coffee.
      </p>
      {buttons}
    </div>
  );
};
